// UI strings of Ääntämisvalmentaja's wiring (app/src/coach/; docs/design/AANTAMISVALMENTAJA.md, work package F):
// the setting in Asetukset → Harjoittelu, the doors (Tänään, the week's Puhu page), the line in Tämä viikko,
// the link in Kielioppi § 1 and lesson 1 (<SoundGuide>), the partner's verdict on a coach take, and the coach's
// screens until the UI package draws them. The per-sound teaching text is the area coachGuide, the live screen's
// coachLive. Style: docs/FINNISH-STYLE.md (sinä-muoto, no jargon, no intro text).
import type { CoachSoundId } from '@valyrio/engine/coach/contract';
import { defineArea } from '../area';

export const coachFi = {
  /** The coach's name: the setting's section, the doors, the hub's title. */
  name: 'Ääntämisvalmentaja',
  /**
   * The sounds as the doors and verdicts name them: the letter(s) as written (shown as High Valyrian), or the
   * name of the voice mode (pitch, stress and range).
   */
  sounds: {
    z: 'z',
    j: 'j',
    v: 'v',
    bdg: 'b d g',
    gh: 'gh',
    q: 'q',
    rh: 'rh',
    lj: 'lj',
    ny: 'ñ',
    y: 'y',
    r: 'r',
    voice: 'äänenkäyttö',
  } satisfies Record<CoachSoundId, string>,
  setting: {
    label: 'Valmentaja',
    on: 'Päällä',
    off: 'Pois',
    /** One quiet line under it. */
    example: 'Valmentaja kuuntelee ääntämistäsi mikrofonin kautta ja näyttää heti, miten äänne onnistuu. Koskee vain sinua.',
  },
  /** The doors' second line: the sound, then the time (Tänään, the week's Puhu page). */
  doorTime: 'noin 5 min',
  /** Tämä viikko, under Puhu: "Ääntämisvalmentaja: r". */
  weekLine: 'Ääntämisvalmentaja',
  /** <SoundGuide id> in Kielioppi § 1 and lesson 1. */
  guideLink: 'Harjoittele valmentajassa',
  verdict: {
    /** Over the partner's three buttons. */
    ask: 'Miltä kuulosti?',
    ok: 'Oikealta',
    near: 'Melkein',
    no: 'Ei vielä',
    /** The verdict as the recorder reads it ("Roosa: melkein oikein"). */
    said: { ok: 'kuulosti oikealta', near: 'melkein oikein', no: 'ei vielä oikein' },
    note: 'Huomio (valinnainen)',
    addNote: 'Lisää huomio',
    noteSave: 'Tallenna huomio',
    remove: 'Poista arvio',
  },
  screens: {
    /** A coach page opened while the coach is off. */
    off: 'Ääntämisvalmentaja on pois päältä.',
    turnOn: 'Ota käyttöön asetuksissa',
    /** Until the UI package draws the page. */
    building: 'Tämä näkymä on vielä tekeillä.',
    sounds: 'Äänteet',
    /** Under a sound in the list: days with practice. */
    practised: (days: number) => (days === 1 ? 'harjoiteltu 1 päivänä' : `harjoiteltu ${days} päivänä`),
    live: 'Harjoittele',
    voice: 'Äänenkäyttö',
    calibration: 'Oman äänen kalibrointi',
    diagnostics: 'Mikrofonin tarkistus',
    credits: 'Lähteet',
    unknownSound: 'Tätä äännettä valmentajassa ei ole.',
    back: 'Ääntämisvalmentaja',
  },
};

export const coachEn: typeof coachFi = {
  name: 'Pronunciation coach',
  sounds: {
    z: 'z',
    j: 'j',
    v: 'v',
    bdg: 'b d g',
    gh: 'gh',
    q: 'q',
    rh: 'rh',
    lj: 'lj',
    ny: 'ñ',
    y: 'y',
    r: 'r',
    voice: 'voice',
  },
  setting: {
    label: 'Coach',
    on: 'On',
    off: 'Off',
    example: 'The coach listens to your pronunciation through the microphone and shows at once how the sound goes. Applies only to you.',
  },
  doorTime: 'about 5 min',
  weekLine: 'Pronunciation coach',
  guideLink: 'Practise with the coach',
  verdict: {
    ask: 'How did it sound?',
    ok: 'Right',
    near: 'Nearly',
    no: 'Not yet',
    said: { ok: 'sounded right', near: 'nearly right', no: 'not right yet' },
    note: 'Note (optional)',
    addNote: 'Add a note',
    noteSave: 'Save note',
    remove: 'Remove verdict',
  },
  screens: {
    off: 'The pronunciation coach is off.',
    turnOn: 'Turn it on in Settings',
    building: 'This page is still being built.',
    sounds: 'Sounds',
    practised: (days: number) => (days === 1 ? 'practised on 1 day' : `practised on ${days} days`),
    live: 'Practise',
    voice: 'Voice',
    calibration: 'Calibrating your voice',
    diagnostics: 'Microphone check',
    credits: 'Sources',
    unknownSound: 'The coach has no such sound.',
    back: 'Pronunciation coach',
  },
};

// Messages.coach (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('coach', coachFi, coachEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof coachFi> {}
}
